import React, { useEffect, useRef } from 'react'
import {
  Modal,
  DefaultButton,
  Text,
  ActionButton,
  Separator,
  DetailsList,
  DetailsListLayoutMode,
  Stack,
  SelectionMode,
  Spinner,
  SpinnerSize,
} from 'office-ui-fabric-react'
import { VideoAssetInfo } from '../../Api/videoAssets'
import moment from 'moment'

type CloseAction = 'delete' | 'rename' | 'none'

interface State {}

interface InputProps {
  isOpen: boolean
  videoAsset: VideoAssetInfo
  shared: boolean
  onClose: (action: CloseAction) => void
}

export class VideoAssetDialog extends React.Component<InputProps, State> {
  render() {
    const videoAsset = this.props.videoAsset
    const detailList = [
      {
        name: 'ID',
        value: videoAsset.id,
      },
      {
        name: 'Title',
        value: videoAsset.title,
      },
      {
        name: 'Creation Date',
        value: moment(videoAsset.creationDate).format('LL'),
      },
      {
        name: 'Creation Time',
        value: moment(videoAsset.creationDate).format('LTS ZZ'),
      },
    ]
    if (Number(videoAsset.durationInSeconds) > 0) {
      detailList.push({
        name: 'Duration',
        value: moment
          .utc(Number(videoAsset.durationInSeconds) * 1e3)
          .format('HH:mm:ss'),
      })
    }
    const shouldDisplayMigration =
      videoAsset.migrationSourceReferer &&
      !videoAsset.migrationSourceReferer.match(/caroda/i)
    if (shouldDisplayMigration) {
      detailList.push({
        name: 'Migrated page url',
        value: videoAsset.migrationSourceReferer,
      })
    }
    if (videoAsset.migrationSourceUrl) {
      detailList.push({
        name: 'Migrated video source url',
        value: videoAsset.migrationSourceUrl,
      })
    }
    return (
      <Modal
        isOpen={this.props.isOpen}
        isDarkOverlay={true}
        isBlocking={true}
        onDismiss={() => this.props.onClose('none')}
        styles={{
          main: {
            width: 600,
            maxWidth: 600,
            minWidth: 120,
          },
        }}
      >
        <Stack>
          <Stack
            styles={{
              root: {
                background: 'var(--color-primary)',
                height: 67,
                paddingLeft: 24,
                paddingTop: 16,
                paddingRight: 48,
              },
            }}
          >
            <Text
              style={{
                color: 'var(--color-white)',
                fontWeight: 600,
                fontSize: 20,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {this.props.videoAsset.title}
            </Text>
          </Stack>
          <ContentPlayer videoAssetId={videoAsset.id} />
          <Stack styles={{ root: { padding: 24 } }} gap={10}>
            <Separator alignContent="start">
              <Text variant="large">Asset Information</Text>
            </Separator>
            <DetailsList
              items={detailList}
              columns={[
                {
                  key: 'column1',
                  name: 'Name',
                  fieldName: 'name',
                  minWidth: 100,
                  maxWidth: 200,
                  isResizable: false,
                },
                {
                  key: 'column2',
                  name: 'Value',
                  fieldName: 'value',
                  minWidth: 100,
                  maxWidth: 200,
                  isResizable: false,
                },
              ]}
              layoutMode={DetailsListLayoutMode.justified}
              selectionMode={SelectionMode.none}
              isHeaderVisible={false}
            />
            <Stack horizontal>
              {!this.props.shared && shouldDisplayMigration && (
                <ActionButton
                  iconProps={{ iconName: 'FileASPX' }}
                  href={videoAsset.migrationSourceReferer}
                  target={'_blank'}
                  text={'View on site'}
                />
              )}
              {!this.props.shared && (
                <ActionButton
                  iconProps={{ iconName: 'edit' }}
                  onClick={() => this.props.onClose('rename')}
                  text={'Rename'}
                />
              )}
              {!this.props.shared && (
                <ActionButton
                  iconProps={{ iconName: 'delete' }}
                  onClick={() => this.props.onClose('delete')}
                  text={'Delete'}
                />
              )}
              <Stack horizontal horizontalAlign="end" style={{ flexGrow: 1 }}>
                <DefaultButton
                  onClick={() => this.props.onClose('none')}
                  text={'Close'}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Modal>
    )
  }
}

function ContentPlayer({ videoAssetId }: { videoAssetId: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const windowAsAny = window as any
    const interval = setInterval(() => {
      if (!windowAsAny.ContentPlayer) {
        return
      }
      clearInterval(interval)
      new windowAsAny.ContentPlayer({
        autoplay: false,
        container: ref.current,
        disablePlayUnmutedScreen: false,
        loop: false,
        muted: false,
        vid: videoAssetId,
        fullScreenContainer: document.body,
      })
    }, 50)
    return () => clearInterval(interval)
  }, [ref, videoAssetId])
  return (
    <div ref={ref} style={{ position: 'relative', aspectRatio: '16 / 9' }}>
      <Spinner
        size={SpinnerSize.large}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />
    </div>
  )
}
